// src/components/UserManagement.jsx - Enhanced with Tabs & Search
import { useEffect, useMemo, useState, useContext} from "react";
import { initializeApp, getApps, getApp } from "firebase/app";
import { getAuth, createUserWithEmailAndPassword } from "firebase/auth";
import {
  collection,
  getDocs,
  doc,
  setDoc,
  deleteDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/firebase";
import { AuthContext } from "../context/AuthContext";
import { firebaseConfig } from "../firebase/firebase";

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);

  const [showCreate, setShowCreate] = useState(false);
  const [email, setEmail] = useState("");
  const [tempPassword, setTempPassword] = useState("");
  const [role, setRole] = useState("user");
  const [selectedGroups, setSelectedGroups] = useState([]);
  const { userData } = useContext(AuthContext);
  const [saving, setSaving] = useState(false);

  const [courses, setCourses] = useState([]);
  const [terms, setTerms] = useState([]);

  // Student editor modal
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [studentCourseIds, setStudentCourseIds] = useState([]);
  const [studentTermId, setStudentTermId] = useState("");

  // NEW: Tab & Search state
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const secondaryAuth = useMemo(() => {
    const name = "secondary-auth";
    const apps = getApps();
    const existing = apps.find((a) => a.name === name);
    const app = existing ? getApp(name) : initializeApp(firebaseConfig, name);
    return getAuth(app);
  }, []);

  useEffect(() => {
    if (!userData) return;
    if (userData.role !== "admin") return;

    (async () => {
      const [usersSnap, groupsSnap, coursesSnap, termsSnap] = await Promise.all([
        getDocs(collection(db, "users")),
        getDocs(collection(db, "groups")),
        getDocs(collection(db, "courses")),
        getDocs(collection(db, "terms")),
      ]);

      setUsers(usersSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setGroups(groupsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setCourses(coursesSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setTerms(termsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    })().catch((e) => console.error("loadAll failed:", e));
  }, [userData]);

  const loadAll = async () => {
    const [usersSnap, coursesSnap, termsSnap] = await Promise.all([
      getDocs(collection(db, "users")),
      getDocs(collection(db, "courses")),
      getDocs(collection(db, "terms")),
    ]);

    setUsers(usersSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    setCourses(coursesSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    setTerms(termsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  const openStudentEditor = (student) => {
    setEditingStudent(student);
    setStudentCourseIds(student?.courseIds || []);
    setStudentTermId(student?.currentTermId || "");
    setShowStudentModal(true);
  };

  const closeStudentEditor = () => {
    setShowStudentModal(false);
    setEditingStudent(null);
    setStudentCourseIds([]);
    setStudentTermId("");
  };

  const toggleStudentCourse = (courseId) => {
    setStudentCourseIds((prev) =>
      prev.includes(courseId) ? prev.filter((x) => x !== courseId) : [...prev, courseId]
    );
  };

  const saveStudentSettings = async () => {
    if (!editingStudent?.id) return alert("No student selected");

    setSaving(true);
    try {
      await updateDoc(doc(db, "users", editingStudent.id), {
        courseIds: studentCourseIds,
        currentTermId: studentTermId || null,
        updatedAt: serverTimestamp(),
      });

      setUsers((prev) =>
        prev.map((u) =>
          u.id === editingStudent.id
            ? { ...u, courseIds: studentCourseIds, currentTermId: studentTermId || null }
            : u
        )
      );

      alert("Student updated.");
      closeStudentEditor();
    } catch (e) {
      console.error(e);
      alert(e?.message || "Failed to save student");
    } finally {
      setSaving(false);
    }
  };

  const toggleGroup = (name) => {
    setSelectedGroups((prev) =>
      prev.includes(name) ? prev.filter((g) => g !== name) : [...prev, name]
    );
  };

  const createUser = async () => {
    if (!email.trim()) return alert("Enter an email");
    if (!tempPassword || tempPassword.length < 6)
      return alert("Password must be at least 6 characters");

    setSaving(true);
    try {
      const cred = await createUserWithEmailAndPassword(
        secondaryAuth,
        email.trim(),
        tempPassword
      );

      await setDoc(doc(db, "users", cred.user.uid), {
        email: email.trim(),
        role,
        groups: role === "admin" || role === "student" ? [] : selectedGroups,
        courseIds: role === "student" ? [] : [],
        currentTermId: role === "student" ? null : null,
        createdAt: serverTimestamp(),
      });

      const createdUser = {
        id: cred.user.uid,
        email: email.trim(),
        role,
        groups: role === "admin" || role === "student" ? [] : selectedGroups,
        courseIds: [],
        currentTermId: null,
      };

      setEmail("");
      setTempPassword("");
      setRole("user");
      setSelectedGroups([]);
      setShowCreate(false);

      setUsers((prev) => [createdUser, ...prev]);

      if (role === "student") {
        openStudentEditor(createdUser);
      } else {
        alert("User created!");
      }

      await loadAll();
    } catch (e) {
      console.error(e);
      alert(e?.message || "Failed to create user");
    } finally {
      setSaving(false);
    }
  };

  const removeUserDoc = async (uid) => {
    if (!window.confirm("Remove this user profile from Firestore?")) return;
    await deleteDoc(doc(db, "users", uid));
    await loadAll();
  };

  // Filter users by tab and search
  const filteredUsers = useMemo(() => {
    let filtered = users;

    // Filter by tab
    if (activeTab !== "all") {
      filtered = filtered.filter((u) => u.role === activeTab);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (u) =>
          u.email?.toLowerCase().includes(query) ||
          u.role?.toLowerCase().includes(query) ||
          u.groups?.some((g) => g.toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [users, activeTab, searchQuery]);

  // Count users by role
  const userCounts = useMemo(() => {
    return {
      all: users.length,
      admin: users.filter((u) => u.role === "admin").length,
      student: users.filter((u) => u.role === "student").length,
      uploader: users.filter((u) => u.role === "uploader").length,
      user: users.filter((u) => u.role === "user").length,
    };
  }, [users]);

  return (
    <div className="admin-card form-container">
      <div className="admin-card-header">
        <div>
          <h2 className="admin-card-title">👤 User Management</h2>
          <p className="admin-card-subtitle">
            Create users and assign resource groups.
          </p>
        </div>

        <button
          className="btn btn-primary"
          type="button"
          onClick={() => setShowCreate((s) => !s)}
        >
          {showCreate ? "Close" : "Create User"}
        </button>
      </div>

      {showCreate && (
        <div className="admin-surface">
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                className="form-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@ciet.edu.au"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Temporary Password</label>
              <input
                className="form-input"
                value={tempPassword}
                onChange={(e) => setTempPassword(e.target.value)}
                placeholder="Min 6 characters"
                type="password"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Role</label>
              <select
                className="form-select"
                value={role}
                onChange={(e) => setRole(e.target.value)}
              >
                <option value="user">User</option>
                <option value="uploader">Uploader</option>
                <option value="student">Student</option>
                <option value="admin">Admin</option>
              </select>
              <div className="admin-hint">
                Admin has full access (no group restriction).
              </div>
            </div>
          </div>

          {role !== "admin" && role !== "student" && (
            <div style={{ marginTop: 14 }}>
              <div className="form-label">Assign Groups</div>
              {groups.length === 0 ? (
                <div className="admin-muted">No groups yet. Create groups first.</div>
              ) : (
                <div className="admin-chip-grid">
                  {groups.map((g) => (
                    <label key={g.id} className="admin-chip">
                      <input
                        type="checkbox"
                        checked={selectedGroups.includes(g.name)}
                        onChange={() => toggleGroup(g.name)}
                      />
                      <span>{g.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="form-actions">
            <button
              className="btn btn-secondary"
              type="button"
              onClick={() => setShowCreate(false)}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              type="button"
              onClick={createUser}
              disabled={saving}
            >
              {saving ? "Creating..." : "Create User"}
            </button>
          </div>
        </div>
      )}

      <div style={{ marginTop: 18 }}>
        <h3 className="admin-subtitle">Existing Users</h3>

        {/* Search Bar */}
        <div style={{ marginBottom: 14 }}>
          <input
            className="form-input"
            type="text"
            placeholder="🔍 Search by email, role, or group..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ width: "100%", maxWidth: "500px" }}
          />
        </div>

        {/* Tabs */}
        <div className="user-tabs">
          <button
            className={`user-tab ${activeTab === "all" ? "is-active" : ""}`}
            onClick={() => setActiveTab("all")}
          >
            All ({userCounts.all})
          </button>
          <button
            className={`user-tab ${activeTab === "admin" ? "is-active" : ""}`}
            onClick={() => setActiveTab("admin")}
          >
            Admins ({userCounts.admin})
          </button>
          <button
            className={`user-tab ${activeTab === "student" ? "is-active" : ""}`}
            onClick={() => setActiveTab("student")}
          >
            Students ({userCounts.student})
          </button>
          <button
            className={`user-tab ${activeTab === "uploader" ? "is-active" : ""}`}
            onClick={() => setActiveTab("uploader")}
          >
            Uploaders ({userCounts.uploader})
          </button>
          <button
            className={`user-tab ${activeTab === "user" ? "is-active" : ""}`}
            onClick={() => setActiveTab("user")}
          >
            Users ({userCounts.user})
          </button>
        </div>

        {/* User Table with Scroll */}
        {filteredUsers.length === 0 ? (
          <div className="admin-muted" style={{ textAlign: "center", padding: "2rem" }}>
            {searchQuery ? "No users found matching your search." : "No users found in Firestore."}
          </div>
        ) : (
          <div className="user-table-container">
            <div className="admin-table">
              <div className="admin-table-row admin-table-head">
                <div>Email</div>
                <div>Role</div>
                <div>Groups</div>
                <div />
              </div>

              {filteredUsers.map((u) => (
                <div key={u.id} className="admin-table-row">
                  <div>{u.email}</div>
                  <div>
                    <span className={`admin-badge ${u.role === "admin" ? "is-admin" : ""}`}>
                      {u.role}
                    </span>
                  </div>
                  <div className="admin-muted">
                    {u.role === "admin"
                      ? "All access"
                      : (u.groups || []).length
                      ? u.groups.join(", ")
                      : "None"}
                  </div>
                  <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
                    {u.role === "student" && (
                      <button
                        className="btn btn-secondary"
                        type="button"
                        onClick={() => openStudentEditor(u)}
                      >
                        Edit Student
                      </button>
                    )}

                    <button
                      className="btn btn-secondary"
                      type="button"
                      onClick={() => removeUserDoc(u.id)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {showStudentModal && (
        <div className="modal-overlay" onClick={closeStudentEditor}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="admin-card-header" style={{ padding: 14 }}>
              <div>
                <h2 className="admin-card-title">👩‍🎓 Student Settings</h2>
                <p className="admin-card-subtitle">
                  {editingStudent?.email || editingStudent?.id}
                </p>
              </div>
              <button className="btn btn-secondary" type="button" onClick={closeStudentEditor}>
                ✕
              </button>
            </div>

            <div className="admin-surface">
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Current term</label>
                  <select
                    className="form-select"
                    value={studentTermId}
                    onChange={(e) => setStudentTermId(e.target.value)}
                  >
                    <option value="">Select…</option>
                    {terms.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ marginTop: 10 }}>
                <div className="form-label">Assign courses</div>

                {courses.length === 0 ? (
                  <div className="admin-muted">No courses found.</div>
                ) : (
                  <div className="admin-chip-grid">
                    {courses.map((c) => (
                      <label key={c.id} className="admin-chip">
                        <input
                          type="checkbox"
                          checked={studentCourseIds.includes(c.id)}
                          onChange={() => toggleStudentCourse(c.id)}
                        />
                        <span>{c.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-actions" style={{ marginTop: 14 }}>
                <button className="btn btn-secondary" type="button" onClick={closeStudentEditor}>
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={saveStudentSettings}
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save Student"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}